import {
  Box,
  Button,
  Flex,
  Grid,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Progress,
  Stack,
  Text,
} from '@chakra-ui/core'
import colors from 'constants/colors'
import keys from 'constants/keys'
import React, { memo } from 'react'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import theme from 'theme'
import { DotsWrapper, Dots } from 'components/helperStyles'
import LocaleText from 'components/LocaleText'
import { PriorityTag } from './PriorityTag'
import { RecurrenceTag } from './RecurrenceTag'
import { ChevronRight, CircleAlert } from 'lucide-react'

// Import COMPLETION_STATUS_COLORS from taskAnalytics for consistency
import { CHART_COLORS } from 'utils/taskAnalytics'
import { SubtaskStatusCounts, TASK_STATUS_ID_BY_VALUE } from 'utils/taskStatusModel'
import { StatusComposition } from 'components/TasksV2/UI/StatusComposition'

interface IProps {
  title: string
  /** One of the 5 task statuses — see `utils/taskStatusModel`. */
  status: number
  /** Per-subtask counts behind `status`; renders the breakdown capsules when provided. */
  statusCounts?: SubtaskStatusCounts
  deadline: string | null
  toDateFormated: string
  fromDateFormated: string
  timelinePercentage: number
  created_by: string
  myUID: string
  isOverdue?: boolean
  onOpen?: () => void
  handleDelete?: () => void
  recurrence_label?: number
  priority?: number
  isPersonalView?: boolean
  isRsc?: boolean
}
export const HubEntityBody = memo(
  ({
    title,
    status,
    statusCounts,
    toDateFormated,
    fromDateFormated,
    timelinePercentage,
    created_by,
    myUID,
    onOpen,
    handleDelete,
    recurrence_label,
    isOverdue,
    priority,
    isPersonalView = false,
    isRsc = false,
  }: IProps) => {
    const isRtl = useSelector(isRtlSelector)
    const statusId = TASK_STATUS_ID_BY_VALUE[status]
    const isDone = () =>
      status === keys.TASK_STATUSES.DONE.value ||
      status === keys.TASK_STATUSES.APPROVED.value ||
      status === keys.TASK_STATUSES.DISAPPROVED.value ||
      status === keys.TASK_STATUSES.REVIEWED_BY_AI.value

    const getColors = () => {
      if (isOverdue && !isDone()) {
        return { timeline: 'tomato', date: 'tomato', title: '#90949C' }
      } else if (isDone()) {
        return { timeline: 'grayMedium', title: '#90949C', date: 'gray2' }
      } else {
        return { timeline: 'gray2', title: 'black', date: 'black' }
      }
    }

    const getStatusColor = (): { bgColor: string; textColor: string } => {
      if (status === keys.TASK_STATUSES.TODO.value) {
        return { bgColor: CHART_COLORS.todo, textColor: CHART_COLORS.todoText }
      } else if (status === keys.TASK_STATUSES.DONE.value) {
        return { bgColor: CHART_COLORS.done, textColor: CHART_COLORS.doneText }
      } else if (status === keys.TASK_STATUSES.INPROGRESS.value) {
        return { bgColor: CHART_COLORS.inProgress, textColor: CHART_COLORS.inProgressText }
      } else if (status === keys.TASK_STATUSES.APPROVED.value) {
        return { bgColor: CHART_COLORS.approved, textColor: CHART_COLORS.approvedText }
      } else if (status === keys.TASK_STATUSES.DISAPPROVED.value) {
        return { bgColor: CHART_COLORS.rejected, textColor: CHART_COLORS.rejectedText }
      } else if (status === keys.TASK_STATUSES.SKIPPED.value) {
        return { bgColor: CHART_COLORS.todo, textColor: CHART_COLORS.todoText } // Skipped treated as TODO
      } else if (status === keys.TASK_STATUSES.REVIEWED_BY_AI.value) {
        return { bgColor: CHART_COLORS.reviewedByAi, textColor: CHART_COLORS.reviewedByAiText }
      } else {
        return { bgColor: CHART_COLORS.todo, textColor: CHART_COLORS.todoText }
      }
    }

    const getProgressBarColorHex = (): string => {
      if (isOverdue && !isDone()) {
        return colors.tomato // Overdue tasks
      } else if (status === keys.TASK_STATUSES.DONE.value) {
        return CHART_COLORS.done
      } else if (status === keys.TASK_STATUSES.APPROVED.value) {
        return CHART_COLORS.approved
      } else if (status === keys.TASK_STATUSES.DISAPPROVED.value) {
        return CHART_COLORS.rejected
      } else if (status === keys.TASK_STATUSES.INPROGRESS.value) {
        return CHART_COLORS.inProgress
      } else if (status === keys.TASK_STATUSES.SKIPPED.value) {
        return CHART_COLORS.todo
      } else if (status === keys.TASK_STATUSES.REVIEWED_BY_AI.value) {
        return CHART_COLORS.reviewedByAiText
      } else {
        return colors.greyLight2
      }
    }

    const getStatusName = (): string => {
      if (status === keys.TASK_STATUSES.TODO.value) {
        return keys.TASK_STATUSES.TODO.localeLiteral
      } else if (status === keys.TASK_STATUSES.DONE.value) {
        return keys.TASK_STATUSES.DONE.localeLiteral
      } else if (status === keys.TASK_STATUSES.INPROGRESS.value) {
        return keys.TASK_STATUSES.INPROGRESS.localeLiteral
      } else if (status === keys.TASK_STATUSES.APPROVED.value) {
        return keys.TASK_STATUSES.APPROVED.localeLiteral
      } else if (status === keys.TASK_STATUSES.DISAPPROVED.value) {
        return keys.TASK_STATUSES.DISAPPROVED.localeLiteral
      } else if (status === keys.TASK_STATUSES.SKIPPED.value) {
        return keys.TASK_STATUSES.SKIPPED.localeLiteral
      } else if (status === keys.TASK_STATUSES.REVIEWED_BY_AI.value) {
        return keys.TASK_STATUSES.REVIEWED_BY_AI.localeLiteral
      } else {
        return keys.TASK_STATUSES.TODO.localeLiteral
      }
    }
    return (
      <>
        {!isPersonalView ? (
          <Grid
            templateColumns={{
              md: isRsc ? '85% 15%' : '55% 30% 15%',
              base: '90% 10%',
            }}
            gridTemplateRows="1fr"
            gap={0.5}
            alignItems="center"
            height="inherit"
            position={{
              md: 'static',
              base: 'relative',
            }}
          >
            <Flex px="20px" flexDir="column" alignItems="flex-start" style={{ rowGap: '5px' }}>
              <Text
                as="span"
                fontFamily="inherit"
                fontWeight="bold"
                fontSize={isPersonalView ? '12px' : '1rem'}
                color={getColors().title}
              >
                {title}
              </Text>
              <Flex align="center" wrap="wrap" style={{ columnGap: '5px', rowGap: '4px' }}>
                <RecurrenceTag recurrence_label={recurrence_label} />
                <PriorityTag priority={priority} />
              </Flex>
            </Flex>

            {!isRsc && (
              <Stack
                gridRow={{
                  md: 'auto',
                  base: 2,
                }}
                px={{
                  md: 0,
                  base: 5,
                }}
                justifyContent="center"
                position={{
                  md: 'relative',
                  base: 'static',
                }}
              >
                <Text
                  textAlign={isRtl ? 'right' : 'left'}
                  dir="ltr"
                  as="div"
                  fontSize={isPersonalView ? '10px' : '.875rem'}
                  color={(theme.colors as any)[getColors().date]}
                >
                  {fromDateFormated} - {toDateFormated}
                </Text>
                <Box
                  maxWidth="90%"
                  display={{
                    md: 'block',
                    base: 'none',
                  }}
                >
                  <Progress color={getColors().timeline} size="md" value={timelinePercentage} borderRadius="10px" />
                </Box>
                {created_by === myUID && (
                  <Box
                    position="absolute"
                    top={{
                      md: '-10px',
                      base: '10px',
                    }}
                    right={{
                      md: !isRtl ? '5px' : 'auto',
                      base: !isRtl ? 'calc(10% + 5px)' : 'auto',
                    }}
                    left={{
                      md: !isRtl ? 'auto' : '5px',
                      base: !isRtl ? 'auto' : 'calc(10% + 5px)',
                    }}
                    zIndex={3}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                  >
                    <Popover placement="left" gutter={30}>
                      <PopoverTrigger>
                        <DotsWrapper>
                          <Dots />
                        </DotsWrapper>
                      </PopoverTrigger>
                      <PopoverContent
                        zIndex={4}
                        width="220px"
                        backgroundColor="#fff"
                        boxShadow="0 2px 4px 0 rgb(187 187 187 / 50%)"
                      >
                        <Button
                          justifyContent="flex-start"
                          fontSize=".875rem"
                          width="100%"
                          onClick={handleDelete ? handleDelete : onOpen}
                          color={colors.red}
                        >
                          <LocaleText text="delete_task_btn" />
                        </Button>
                      </PopoverContent>
                    </Popover>
                  </Box>
                )}
              </Stack>
            )}
            <Flex
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              bg={getStatusColor().bgColor}
              height="100%"
              gridRow="span 2"
              style={{ gap: '6px' }}
            >
              <Text
                as="span"
                color={getStatusColor().textColor}
                fontSize=".75rem"
                fontWeight="bold"
                textShadow="0 0 6px 0 rgba(29,33,41,0.15)"
                display={{
                  md: 'inline',
                  base: 'none',
                }}
              >
                <LocaleText text={getStatusName()} />
              </Text>
              {/* Explains what the status is made of: `done` = 1 done + 2 approved + 1 w/ reservation.
                  Hidden by the component itself for non-terminal statuses and single-outcome tasks. */}
              {statusCounts && statusId && (
                <Box display={{ md: 'block', base: 'none' }}>
                  <StatusComposition taskStatus={statusId} counts={statusCounts} variant="hub" />
                </Box>
              )}
            </Flex>
          </Grid>
        ) : (
          <Flex alignItems="center" justifyContent="space-between" p="10px" position="relative">
            <Flex alignItems="center" style={{ columnGap: '10px' }}>
              <Box
                bg={getProgressBarColorHex()}
                borderRadius="50%"
                width="12px"
                height="12px"
                flexShrink={0}
                alignItems="center"
              />

              <Flex flexDir="column" alignItems="flex-start">
                <Text as="span" fontFamily="inherit" fontWeight="bold" fontSize={'1rem'} color={getColors().title}>
                  {title}
                </Text>

                <Text
                  textAlign={isRtl ? 'right' : 'left'}
                  dir="ltr"
                  fontSize={'11px'}
                  color={(theme.colors as any)[getColors().timeline]}
                >
                  {fromDateFormated} - {toDateFormated}
                </Text>
              </Flex>
            </Flex>
            {/* <Flex alignItems="center" style={{ columnGap: '10px' }}>
              <RecurrenceTag recurrence_label={recurrence_label} />
              <PriorityTag priority={priority} />
            </Flex> */}
            {isOverdue && !isDone() && <CircleAlert size={20} color={colors.tomato} />}
            <ChevronRight size={20} color={colors.greyMain} style={{ transform: isRtl ? 'rotate(180deg)' : 'none' }} />
            <Box
              position="absolute"
              w="100%"
              bottom="-3px"
              left="0"
              right="0"
              height="0.3rem"
              bg="#F0F0F0"
              borderRadius="10px"
              overflow="hidden"
            >
              <Box
                height="100%"
                width={`${timelinePercentage}%`}
                bg={getProgressBarColorHex()}
                transition="width 0.3s ease"
                borderRadius="10px"
              />
            </Box>
          </Flex>
        )}
      </>
    )
  }
)
